import * as React from 'react';
import {
  List,
  Datagrid,
  Edit,
  Create,
  SimpleForm,
  TextField,
  EditButton,
  TextInput,
  BooleanInput,
  FunctionField,
  useTranslate,
  useLocaleState,
  required,
  useEditContext,
} from 'react-admin';

import EmptyList from '../components/misc/EmptyList';
import SourceLanguageBanner from '../components/form/SourceLanguageBanner';
import { useSourceLanguage } from '../hooks/useSourceLanguage';

const entity = 'categories';

export const CategoryList = (props) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const localeNameKey = locale === 'tr' ? 'name.tr' : 'name.en';

  return (
    <List
      {...props}
      title={translate('categories.title', { _: 'Kategoriler' })}
      empty={<EmptyList entity={entity} />}
      exporter={false}
      sort={{ field: 'order', order: 'ASC' }}
    >
      <Datagrid>
        <TextField
          source={localeNameKey}
          label={translate('categories.name', { _: 'Kategori Adı' })}
        />
        <FunctionField
          source="active"
          label={translate('categories.active', { _: 'Aktif mi?' })}
          render={(r) =>
            r.active
              ? translate('categories.yes', { _: 'Evet' })
              : translate('categories.no', { _: 'Hayır' })
          }
        />
        <TextField source="order" label={translate('categories.order', { _: 'Sıra' })} />
        <EditButton />
      </Datagrid>
    </List>
  );
};

const CategoryFormFields = ({ isEdit = false, record }) => {
  const translate = useTranslate();
  const { localizedSource } = useSourceLanguage({ isEdit, record });

  return (
    <>
      <SourceLanguageBanner isEdit={isEdit} record={record} />
      <TextInput source="id" label="ID" disabled={isEdit} validate={required()} />
      {isEdit ? (
        <BooleanInput
          source="active"
          label={translate('categories.active', { _: 'Aktif mi?' })}
          helperText={translate('categories.active_help', {
            _: 'Bu kategoriyi devre dışı bırakmak istiyorsanız bu seçeneğin işaretini kaldırın.',
          })}
        />
      ) : (
        <BooleanInput source="active" defaultValue={true} sx={{ display: 'none' }} />
      )}
      <TextInput
        source={localizedSource('name')}
        label={translate('categories.name', { _: 'Kategori Adı' })}
        fullWidth
        validate={required()}
      />
      <TextInput
        source="order"
        label={translate('categories.order', { _: 'Sıra' })}
        fullWidth
        helperText={translate('categories.order_help', {
          _: 'Kategorinin sıralamasını belirlemek için bir sayı giriniz.',
        })}
      />
    </>
  );
};

const CategoryEditForm = () => {
  const { record } = useEditContext();
  return (
    <SimpleForm redirect={`/${entity}`}>
      <CategoryFormFields isEdit record={record} />
    </SimpleForm>
  );
};

export const CategoryEdit = (props) => {
  const translate = useTranslate();

  return (
    <Edit
      title={translate('categories.edit_category', { _: 'Kategoriyi Düzenle' })}
      {...props}
      transform={(data, { previousData }) => ({
        ...data,
        sourceLang: previousData?.sourceLanguage || 'tr',
      })}
    >
      <CategoryEditForm />
    </Edit>
  );
};

export const CategoryCreate = (props) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();

  return (
    <Create
      title={translate('categories.new_category', { _: 'Yeni Kategori' })}
      {...props}
      transform={(data) => ({
        ...data,
        sourceLang: locale || 'tr',
      })}
    >
      <SimpleForm redirect={`/${entity}`}>
        <CategoryFormFields isEdit={false} />
      </SimpleForm>
    </Create>
  );
};
